import { IScope, INgModelController } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IModalService } from "angular-ui-bootstrap";
import { DataSourceService } from "@gtmhub/datasources/services/dataSourceService";
import { InsightService, IInsight, IConnectionData } from "@gtmhub/insights";
import { ParameterService } from "@gtmhub/parameters/parameterService";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IDataSourceBlueprint } from "@gtmhub/datasources/models/models";
import { ICollection } from "@gtmhub/models";
import { IParameter } from "@gtmhub/parameters/models";
import {
  IInsightSelectorData,
  IDynamicItemSettings,
  IBrowseInsightsContext,
  IExecuteInsightContext,
  IBrowseConnectionsActionsParams,
  IBuildInsightActionParams,
  IExecuteInsightActionParams,
  IBrowseInsightsActionParams,
  IUpdateInsightSelectorDataActionParams,
  IChangeStateActionParams,
} from "./models";
import { INgRedux } from "@gtmhub/state-management";
import { toIdMap } from "@gtmhub/util";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { byTextField } from "@gtmhub/util/sorting";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { getInsightSelectorData, isInsightCodeless } from "../../util";
import { AssigneeActions, IAssigneesStoreState } from "../../../assignees";
import { ISessionsIdMap, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { InsightSelectorService } from "@gtmhub/insights";

export interface IInsightSelectorScope extends IScope, IExtendedRootScopeService {
  insightTitle: string;
  indicators: {
    loading: boolean;
    loadingParams: boolean;
  };
  insight: IInsight;
  confirmedInsight: IInsight;
  data: IInsightSelectorData;
  readOnly: boolean;
  assigneesIdMap: IAssigneeIdMap;
  sessionsIdMap: ISessionsIdMap;
  kpiId: string;
  reduxStates: {
    reduxStatesInitialised: boolean;
    reduxStateError: IUIError;
  };
  editInsightFilters(): void;
  openBrowseConnectionsModal(): void;
  removeInsightFromItem(): void;
  goToInsight(): void;
  openInsight(): void;
}

export class InsightSelectorCtrl {
  public static $inject = [
    "$scope",
    "$state",
    "$uibModal",
    "DataSourceService",
    "InsightService",
    "ParameterService",
    "UIErrorHandlingService",
    "$ngRedux",
    "AssigneeActions",
    "PlanningSessionsActions",
    "InsightSelectorService",
  ];
  constructor(
    private $scope: IInsightSelectorScope,
    private $state: StateService,
    private $uibModal: IModalService,
    private dataSourceService: DataSourceService,
    private insightService: InsightService,
    private parameterService: ParameterService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $ngRedux: INgRedux,
    private assigneeActions: AssigneeActions,
    private planningSessionsActions: PlanningSessionsActions,
    private insightSelectorService: InsightSelectorService
  ) {
    this.$scope.reduxStates = {
      reduxStatesInitialised: false,
      reduxStateError: null,
    };
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    this.init();
  }

  public setNgModelController = (newNgModelCtrl: INgModelController): void => {
    this.ngModelCtrl = newNgModelCtrl;
  };

  private insightFilters: IParameter[] = [];
  private ngModelCtrl: INgModelController = null;

  private bindStateToScope = (state: ISessionsStoreState & IAssigneesStoreState) => {
    const { map: assignees, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
    const { items: sessions, isFetched: sessionsFetched, error: sessionsError } = state.sessions;

    return {
      reduxStates: { reduxStatesInitialised: assigneesFetched && sessionsFetched, reduxStateError: assigneesError || sessionsError },
      sessionsIdMap: toIdMap(sessions),
      assigneesIdMap: assignees,
    };
  };

  private init = (): void => {
    const unsubscribe = this.$ngRedux.connect(this.bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    this.$scope.indicators = {
      loading: false,
      loadingParams: false,
    };

    this.$scope.editInsightFilters = this.editInsightFilters;
    this.$scope.openBrowseConnectionsModal = this.openBrowseConnectionsModal;
    this.$scope.removeInsightFromItem = this.removeInsightFromItem;
    this.$scope.goToInsight = this.goToInsight;
    this.$scope.openInsight = this.openInsight;

    if (this.$scope.data && this.$scope.data.insightName) {
      this.$scope
        .watchUntil(() => this.$scope.reduxStates.reduxStatesInitialised || this.$scope.reduxStates.reduxStateError)
        .then(() => (this.$scope.reduxStates.reduxStateError ? this.uiErrorHandlingService.handleModal(this.$scope.reduxStates.reduxStateError) : this.loadDataOnEdit()))
        .catch((err) => this.uiErrorHandlingService.handleModal(err));
    }
  };

  private openInsight = (): void => {
    this.$scope.insight = this.$scope.confirmedInsight;

    if (isInsightCodeless(this.$scope.insight)) {
      this.openBuildInsightModal(this.$scope.insight.connection);
    } else {
      this.$state.go("gtmhub.insight", { name: this.$scope.insight.name, dashboardId: null, kpiId: this.$scope.kpiId });
    }
  };

  private getFilterFromItemSettings = (name: string): IDynamicItemSettings => {
    return this.$scope.data.settings.find((filter) => filter.name === name);
  };

  // this will update filters in item settings in case insight used is different from codeless (codeless insights support no filters) s.t.;
  // if some are removed from insight => they won't be displayed
  // if new ones are added to insight => they will be displayed with default values or no values
  private setItemFilters = (): void => {
    const formattedFilters: IDynamicItemSettings[] = [];

    this.insightFilters.forEach((insightFilter) => {
      const formattedFilter: IDynamicItemSettings = {
        name: insightFilter.key,
        title: insightFilter.title,
        value: insightFilter.defaultValue,
        valueDisplayName: "",
        type: insightFilter.selectorType,
      };

      const itemFilter = this.getFilterFromItemSettings(insightFilter.key);

      if (itemFilter) {
        formattedFilter.value = itemFilter.value;
        formattedFilter.valueDisplayName = this.toValueDisplayName(itemFilter.name, itemFilter.valueDisplayName || itemFilter.value);
      }

      formattedFilters.push(formattedFilter);
    });

    // sort so that filters are always in alphabetic order
    this.$scope.data.settings = formattedFilters.sort(byTextField("title"));
  };

  private toValueDisplayName = (key: string, ids: string): string => {
    return ids
      .split(",")
      .map((id) => {
        let displayName: string;

        switch (key) {
          case "team":
            displayName = this.$scope.assigneesIdMap[id]?.name;
            break;
          case "session":
            displayName = this.$scope.sessionsIdMap[id]?.title;
            break;
          // if there is no match in the switch it will just return
          // what it is by default on the next line
        }
        return displayName || id;
      })
      .toString();
  };

  // extracts filters from metric settings if insight used is codeless insight (supports no insight filters)
  private getItemFilters = (): void => {
    const formattedFilters: IDynamicItemSettings[] = [];

    // value is set to empty string as we dont support filters for codeless insights, however we should display the filters with value 'None Selected'
    this.$scope.data.settings.forEach((filter) => {
      const formattedFilter: IDynamicItemSettings = {
        name: filter.name,
        title: filter.name,
        value: "",
        valueDisplayName: null,
        type: "text",
      };

      formattedFilters.push(formattedFilter);
    });

    // sort in alphabetic order
    this.$scope.data.settings = formattedFilters.sort(byTextField("title"));
  };

  private getDataSourceBlueprintName = (insight: IInsight): string => {
    return insight.usedEntities && insight.usedEntities.length ? insight.usedEntities[0].dataSourceBlueprintName : null;
  };

  private getInsightConnectionData = (blueprint: IDataSourceBlueprint): IConnectionData => {
    return {
      name: blueprint.name,
      title: blueprint.title,
      logoUrl: blueprint.logoUrl,
    };
  };

  private loadConnectionData = (datasourceBlueprintName: string) => {
    if (datasourceBlueprintName) {
      this.dataSourceService.getBlueprint(datasourceBlueprintName).then(
        (blueprint: IDataSourceBlueprint) => {
          this.$scope.indicators.loading = false;
          this.$scope.insight.connection = this.getInsightConnectionData(blueprint);
        },
        (err) => this.handleIndicatorError(err, "loading")
      );
    } else {
      this.$scope.indicators.loading = false;
      this.$scope.insight.connection = {
        name: null,
        title: null,
        logoUrl: null,
      };
    }
  };

  private loadDataOnEdit = () => {
    this.$scope.indicators.loading = true;

    this.insightService.getInsightSafe(this.$scope.data.insightName).then(
      (insight: IInsight) => {
        this.initFromInsight(insight);
      },
      (err) => this.handleIndicatorError(err, "loading")
    );
  };

  private initFromInsight = (insight: IInsight): void => {
    this.setInsightToScope(insight);
    this.processInsightFilters(insight);
    this.getConnectionData(insight);
  };

  private setInsightToScope = (insight: IInsight): void => {
    this.$scope.insight = insight;
    this.$scope.confirmedInsight = insight;
  };

  private processInsightFilters = (insight: IInsight): void => {
    if (!isInsightCodeless(insight)) {
      this.$scope.indicators.loadingParams = true;
      this.parameterService.getParameters().then(
        (parameters: ICollection<IParameter>) => {
          this.$scope.indicators.loadingParams = false;
          this.insightFilters = this.insightService.getInsightUsedFilters(this.$scope.insight, parameters.items);
          this.setItemFilters();
        },
        (err) => this.handleIndicatorError(err, "loadingParams")
      );
    } else {
      this.getItemFilters();
    }
  };

  private getConnectionData = (insight: IInsight): void => {
    this.$scope.data.datasourceBlueprintName = this.getDataSourceBlueprintName(insight);
    this.loadConnectionData(this.$scope.data.datasourceBlueprintName);
  };

  private editInsightFilters = () => {
    if (this.$scope.readOnly) {
      return;
    }

    this.$scope.insight = this.$scope.confirmedInsight;

    if (isInsightCodeless(this.$scope.insight)) {
      this.openBuildInsightModal(this.$scope.insight.connection);
    } else {
      this.openExecuteInsightModal(this.$scope.insight);
    }
  };

  private openExecuteInsightModal = (insight: IInsight): void => {
    this.$uibModal
      .open({
        templateUrl: require("./execute-insight/execute-insight.jade"),
        windowClass: "medium-small",
        resolve: {
          executeInsightContext: (): IExecuteInsightContext => ({
            insight,
            data: this.$scope.data,
          }),
        },
        controller: "ExecuteInsightCtrl",
      })
      .result.then((params: IBrowseConnectionsActionsParams | IBrowseInsightsActionParams | IUpdateInsightSelectorDataActionParams) => {
        this.onModalClosed(params);
      });
  };

  private openBrowseConnectionsModal = (): void => {
    this.$uibModal
      .open({
        templateUrl: require("./browse-connections/browse-connections.jade"),
        windowClass: "medium-small",
        controller: "BrowseConnectionsCtrl",
      })
      .result.then((params: IBrowseInsightsActionParams) => {
        this.onModalClosed(params);
      });
  };

  private openBrowseInsightsModal = (connectionData: IConnectionData): void => {
    this.$uibModal
      .open({
        templateUrl: require("./browse-insights/browse-insights.jade"),
        windowClass: "medium-small",
        resolve: {
          browseInsightsContext: (): IBrowseInsightsContext => ({
            connectionData,
          }),
        },
        controller: "BrowseInsightsCtrl",
      })
      .result.then(
        (params: IBrowseConnectionsActionsParams | IBuildInsightActionParams | IExecuteInsightActionParams) => {
          this.onModalClosed(params);
        },
        // when dismissed
        (reason: IChangeStateActionParams) => {
          if (reason && reason.stateName && reason.action === "changeState") {
            this.$state.go(reason.stateName);
          }
        }
      );
  };

  private openBuildInsightModal = (connectionData: IConnectionData): void => {
    this.insightSelectorService
      .openBuildInsightModal({ insight: this.$scope.confirmedInsight, connectionData, canGoBack: true })
      .then((params: IUpdateInsightSelectorDataActionParams | IBrowseInsightsActionParams) => {
        this.onModalClosed(params);
      });
  };

  private onModalClosed = (
    params:
      | IBrowseConnectionsActionsParams
      | IBrowseInsightsActionParams
      | IBuildInsightActionParams
      | IExecuteInsightActionParams
      | IUpdateInsightSelectorDataActionParams
  ): void => {
    switch (params.action) {
      case "browseConnections":
        this.openBrowseConnectionsModal();
        break;
      case "browseInsights":
        this.openBrowseInsightsModal(params.connectionData);
        break;
      case "buildInsight":
        this.openBuildInsightModal(params.connectionData);
        break;
      case "executeInsight":
        this.$scope.insight = params.insight;
        this.openExecuteInsightModal(params.insight);
        break;
      case "updateInsightSelectorData":
        this.updateSelectorData(params);
        break;

      default:
        break;
    }
  };

  private removeInsightFromItem = () => {
    if (this.$scope.readOnly) {
      return;
    }

    const result: IInsightSelectorData = {
      insightName: "",
      initialValue: null,
      settings: [],
      datasourceBlueprintName: null,
      fieldName: "",
    };

    this.$scope.insightTitle = null;
    this.setInsightToScope(null);

    this.ngModelCtrl.$setViewValue(result);
  };

  private updateSelectorData = (params: IUpdateInsightSelectorDataActionParams): void => {
    const result = getInsightSelectorData(params);

    this.$scope.insightTitle = params.insight.title;

    this.setInsightToScope(params.insight);

    this.ngModelCtrl.$setViewValue(result);
  };

  private goToInsight = (): void => {
    if (this.$scope.confirmedInsight && !this.$scope.confirmedInsight.isShadow) {
      const options = { name: this.$scope.confirmedInsight.name };

      this.$scope.confirmedInsight.formatting && this.$scope.confirmedInsight.formatting.isCodeless
        ? this.$state.go("gtmhub.insightCodelessEditor", options)
        : this.$state.go("gtmhub.insight", options);
    }
  };

  private handleIndicatorError = <E>(err: E, indicator: string, inline = false): void => {
    this.uiErrorHandlingService.handleIndicator(err, { indicator, inline });
  };
}
